/**
 * Phase PWA — Sync engine for the inspection queue.
 *
 * Walks each queue row through the step machine:
 *   metadata → POST /api/admin/rentals/{id}/inspection
 *   photos   → POST /api/admin/rentals/{id}/inspection/photos
 *   done     → row deleted
 *
 * Failure semantics:
 *   * 4xx → mark `last_error`, stop. Surfaced in the UI dropdown.
 *   * 5xx / network → leave step as-is, attempts++. Replay on next trigger.
 *
 * Triggers (all universal — Background Sync is a bonus on top):
 *   * `online` event
 *   * `visibilitychange` → visible
 *   * 30s soft poll while a Manager portal page is open
 *   * Direct call from `submitInspection` after a failed network POST.
 */

import { listQueue, updateRow, deleteRow } from "./inspection-queue";
import api, { formatApiError } from "@/lib/api";

let _flushing = false;
const _listeners = new Set();

export function onQueueChange(fn) {
    _listeners.add(fn);
    return () => _listeners.delete(fn);
}

function _notify() {
    _listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

async function _postMetadata(row) {
    const { rental_id, metadata } = row;
    await api.post(`/admin/rentals/${rental_id}/inspection`, metadata);
}

async function _postPhotos(row) {
    if (!row.photos || row.photos.length === 0) return;
    const fd = new FormData();
    row.photos.forEach((blob, idx) => {
        const name = blob.name || `inspection-${idx}.jpg`;
        fd.append("photos", blob, name);
    });
    await api.post(`/admin/rentals/${row.rental_id}/inspection/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
}

export async function flushInspectionQueue() {
    if (_flushing) return { skipped: true };
    if (!navigator.onLine) return { skipped: true, reason: "offline" };
    _flushing = true;
    let synced = 0, failed = 0;
    try {
        const rows = await listQueue();
        for (const row of rows) {
            if (row.last_error) continue; // surfaced for manual recovery
            try {
                if (row.step === "metadata") {
                    await _postMetadata(row);
                    row.step = "photos";
                    await updateRow(row);
                }
                if (row.step === "photos") {
                    await _postPhotos(row);
                    row.step = "done";
                    await updateRow(row);
                }
                if (row.step === "done") {
                    await deleteRow(row.id);
                    synced++;
                }
            } catch (e) {
                const status = e?.response?.status;
                if (status && status >= 400 && status < 500) {
                    // Validation error — surface to admin, stop auto-retry.
                    row.last_error = formatApiError(e);
                    row.attempts = (row.attempts || 0) + 1;
                    await updateRow(row);
                    failed++;
                } else {
                    // Network / 5xx — leave queued; advance attempts only.
                    row.attempts = (row.attempts || 0) + 1;
                    await updateRow(row);
                }
            }
        }
    } finally {
        _flushing = false;
        _notify();
    }
    return { synced, failed };
}

let _intervalId = null;
let _setupDone = false;

export function startSyncEngine() {
    if (typeof window === "undefined") return;
    if (_setupDone) return;
    _setupDone = true;
    window.addEventListener("online", () => { flushInspectionQueue(); });
    document.addEventListener("visibilitychange", () => {
        if (!document.hidden) flushInspectionQueue();
    });
    // Soft poll — cheap when the queue is empty.
    if (!_intervalId) _intervalId = setInterval(() => {
        if (navigator.onLine) flushInspectionQueue();
    }, 30_000);

    // Best-effort Background Sync registration (Chrome/Android/Edge only).
    // Wrapped to swallow Promise rejection — Background Sync can throw on
    // iOS/Safari/Firefox, incognito, denied permissions, or when the page
    // is not running as an installed PWA. The fallback triggers above
    // (`online`, `visibilitychange`, 30s soft poll) keep the queue moving.
    _registerBackgroundSync();
}

async function _registerBackgroundSync() {
    if (!("serviceWorker" in navigator) || !("SyncManager" in window)) return;
    try {
        const reg = await navigator.serviceWorker.ready;
        if (reg && reg.sync && typeof reg.sync.register === "function") {
            // Tag kept short (<24 chars). Must match sw.js's `sync` listener.
            await reg.sync.register("rf-insp-sync");
        }
    } catch (e) {
        // Non-fatal — see comment above startSyncEngine's call site.
        console.warn("Background Sync registration failed (non-fatal):", e);
    }
}
